/**
 * AudioSubsystem — audio playback for the player runtime.
 *
 * On level load: scans for AudioListener and AudioSource components,
 * pre-loads clips marked `playOnAwake`, starts playback.
 * On each update: syncs listener position from transform, updates
 * spatial source positions, runs doppler calculation.
 *
 * Loads audio files via AssetSubsystem (HTTP from exported bundle).
 * Uses AudioEngine from @engine/audio for Web Audio API management.
 *
 * Zero editor dependencies.
 *
 * @module @engine/player
 */

import {
  Subsystem,
  SubsystemPriority,
  type SubsystemConfig,
  type Level,
  type GUID,
  type AudioSource,
} from '@engine/core';
import {
  AudioEngine,
  type AudioMixerDefinition,
  createDefaultMixerDefinition,
} from '@engine/audio';

import type { AssetSubsystem } from './AssetSubsystem';
import { IS_ENGINE_PLAYER, hostAudio } from '../env';
import { HostAudioEngine } from './HostAudioEngine';

// ============================================================================
// TYPES
// ============================================================================

interface VoiceMapping {
  voiceId: number;
  clipGuid: string;
}

// ============================================================================
// SUBSYSTEM
// ============================================================================

export class AudioSubsystem extends Subsystem {
  readonly config: SubsystemConfig = {
    name: 'audio',
    priority: SubsystemPriority.Presentation - 5,
    optional: true,
    dependencies: ['assets'],
  };

  private _engine: AudioEngine;
  private _guidToVoice = new Map<GUID, VoiceMapping>();
  private _activeListenerGuid: GUID | null = null;
  private _level: Level | null = null;
  private _ready = false;
  private _eventUnsubs: (() => void)[] = [];

  constructor() {
    super();
    // In the native engine player, route audio through miniaudio (audioHost) instead of
    // Web Audio. The cast is intentional: HostAudioEngine implements only the slice of the
    // AudioEngine surface AudioSubsystem uses. Browser/editor keep the real AudioEngine.
    this._engine = (IS_ENGINE_PLAYER && hostAudio?.isAvailable)
      ? (new HostAudioEngine(hostAudio) as unknown as AudioEngine)
      : new AudioEngine({
          sampleRate: 0,
          latencyHint: 'interactive',
          maxVoices: 64,
          defaultPanningModel: 'HRTF',
          defaultDistanceModel: 'inverse',
        });
  }

  /** The underlying AudioEngine */
  get engine(): AudioEngine {
    return this._engine;
  }

  // ── GUID-addressed per-source control — backs engine.audio play/stop/pause/resume(entity) ──
  /** (Re)start the AudioSource's own clip as a tracked voice (stops an existing one first). */
  playSource(guid: GUID): void {
    const values = this._level?.findByGuid(guid)?.getComponentData("core.AudioSource") as AudioSource | undefined;
    if (!values) return;
    this.stopSource(guid);
    void this._playSource(guid, values);
  }
  stopSource(guid: GUID): void {
    const m = this._guidToVoice.get(guid);
    if (m) { this._engine.stopVoice(m.voiceId); this._guidToVoice.delete(guid); }
  }
  pauseSource(guid: GUID): void {
    const m = this._guidToVoice.get(guid);
    if (m) this._engine.pauseVoice(m.voiceId);
  }
  resumeSource(guid: GUID): void {
    const m = this._guidToVoice.get(guid);
    if (m) this._engine.resumeVoice(m.voiceId);
  }

  // ==========================================================================
  // LIFECYCLE
  // ==========================================================================

  async onInitialize(): Promise<void> {
    console.log('[AudioSubsystem] Initialized');
  }

  onUpdate(dt: number): void {
    if (!this._ready || !this._level) return;
    if (!this._engine.initialized) return;

    this._updateListener();
    this._updateSources(dt);
    this._engine.updateDoppler(dt);
    this._engine.updateMetering();
  }

  onShutdown(): void {
    this._engine.shutdown();
    this._guidToVoice.clear();
    this._activeListenerGuid = null;
    this._level = null;
    this._ready = false;
  }

  // ==========================================================================
  // PUBLIC API
  // ==========================================================================

  /**
   * Set the active level — loads playOnAwake clips and starts playback.
   */
  async setLevel(level: Level | null): Promise<void> {
    // Stop everything from previous level
    this._ready = false;
    this._engine.stopAll();
    this._guidToVoice.clear();
    this._activeListenerGuid = null;
    for (const unsub of this._eventUnsubs) unsub();
    this._eventUnsubs = [];
    this._level = level;

    if (!level) return;

    // Ensure engine initialised
    if (!this._engine.initialized) {
      try {
        await this._engine.initialize(createDefaultMixerDefinition());
      } catch (err) {
        console.warn('[AudioSubsystem] Failed to initialize AudioEngine:', err);
        return;
      }
    }

    // Verify there's an active AudioListener
    const hasListener = level.getAllObjects().some((go) => {
      const vals = go.getComponentData('core.AudioListener');
      return !!vals?.enabled;
    });
    if (!hasListener) {
      console.warn('[AudioSubsystem] No active AudioListener in level — audio disabled');
      return;
    }

    // Pre-load playOnAwake clips
    const pendingSources: { guid: GUID; values: AudioSource }[] = [];
    for (const go of level.getAllObjects()) {
      const values = go.getComponentData('core.AudioSource');
      if (!values) continue;
      if (values.playOnAwake && values.clip) {
        await this._loadClipFromManifest(values.clip);
        pendingSources.push({ guid: go.guid, values });
      }
    }

    // Mark ready — onUpdate can now run
    this._ready = true;

    // Start playback of all playOnAwake sources
    for (const { guid, values } of pendingSources) {
      await this._playSource(guid, values);
    }

    console.log(`[AudioSubsystem] Level ready, ${pendingSources.length} playOnAwake sources`);

    // Subscribe to runtime scene changes
    this._eventUnsubs.push(
      level.events.onObjectAdded(({ gameObject }) => {
        const values = gameObject.getComponentData('core.AudioSource');
        if (values?.playOnAwake && values.clip) {
          this._playSource(gameObject.guid, values);
        }
      }, ['core.AudioSource']),
      level.events.onObjectRemoved(({ gameObject }) => {
        const mapping = this._guidToVoice.get(gameObject.guid);
        if (mapping) {
          this._engine.stopVoice(mapping.voiceId);
          this._guidToVoice.delete(gameObject.guid);
        }
      }),
    );
  }

  // ==========================================================================
  // INTERNAL — clip loading
  // ==========================================================================

  /**
   * Load a clip from the exported asset bundle via AssetSubsystem.
   */
  private async _loadClipFromManifest(clipGuid: string): Promise<boolean> {
    if (this._engine.isClipLoaded(clipGuid)) return true;

    try {
      const assets = this.getSubsystem<AssetSubsystem>('assets');
      const manifest = assets.getManifest();
      const path = manifest?.assets.audio?.[clipGuid];
      if (!path) {
        console.warn(`[AudioSubsystem] Clip not in manifest: ${clipGuid}`);
        return false;
      }

      const url = assets.resolveUrl(path);
      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();
      await this._engine.loadClip(clipGuid, arrayBuffer);
      return true;
    } catch (err) {
      console.warn(`[AudioSubsystem] Failed to load clip ${clipGuid}:`, err);
      return false;
    }
  }

  // ==========================================================================
  // INTERNAL — playback
  // ==========================================================================

  private async _playSource(goGuid: GUID, values: AudioSource): Promise<void> {
    const clipGuid = values.clip;
    if (!clipGuid) return;

    // Dedup: a playOnAwake source is started by BOTH setLevel's initial loop and the
    // objectAdded event flushed on the first frame. Reserve the guid synchronously (before
    // any await) so the second call bails — otherwise it plays twice (audible in the native
    // player, where the first LoadSound decodes synchronously and offsets the two by ~0.5s).
    if (this._guidToVoice.has(goGuid)) return;
    this._guidToVoice.set(goGuid, { voiceId: -1, clipGuid });

    const loaded = await this._loadClipFromManifest(clipGuid);
    if (!loaded) { this._guidToVoice.delete(goGuid); return; }

    // Get initial position from transform
    const go = this._level?.getAllObjects().find((o) => o.guid === goGuid);
    const srcX = go?.transform.position.x ?? 0;
    const srcY = go?.transform.position.y ?? 0;

    const spatialEnabled = values.spatialBlend ?? false;
    const voice = this._engine.play(clipGuid, {
      volume: values.mute ? 0 : (values.volume ?? 1),
      pitch: values.pitch ?? 1,
      loop: values.loop ?? false,
      busId: values.outputBus ?? 'master',
      fadeIn: values.fadeInTime ?? 0,
      priority: values.priority ?? 128,
      position: spatialEnabled ? { x: srcX, y: 0, z: -srcY } : undefined,
      spatial: spatialEnabled
        ? {
            enabled: true,
            panningModel: (values.panningModel as 'HRTF' | 'equalpower') ?? 'HRTF',
            distanceModel: (values.distanceModel as 'linear' | 'inverse' | 'exponential') ?? 'inverse',
            refDistance: values.minDistance ?? 1,
            maxDistance: values.maxDistance ?? 100,
            rolloffFactor: values.rolloffFactor ?? 1,
            coneInnerAngle: values.coneInnerAngle ?? 360,
            coneOuterAngle: values.coneOuterAngle ?? 360,
            coneOuterGain: values.coneOuterGain ?? 0,
            dopplerFactor: values.dopplerLevel ?? 1,
          }
        : undefined,
    });

    if (voice) {
      this._guidToVoice.set(goGuid, { voiceId: voice.id, clipGuid });
    } else {
      this._guidToVoice.delete(goGuid); // load/play failed — release the reservation
    }
  }

  // ==========================================================================
  // INTERNAL — update
  // ==========================================================================

  private _updateListener(): void {
    if (!this._level) return;

    for (const go of this._level.getAllObjects()) {
      const vals = go.getComponentData('core.AudioListener');
      if (!vals?.enabled) continue;

      const listenerVolume = vals.volume ?? 1;
      this._engine.setMasterVolume(listenerVolume);

      const dopplerFactor = vals.dopplerFactor ?? 0;
      const speedOfSound = vals.speedOfSound ?? 343;
      this._engine.setDopplerConfig(dopplerFactor, speedOfSound);

      const invertX = vals.invertX ?? false;
      const invertY = vals.invertY ?? false;
      const rotOffset = (vals.rotationOffset ?? 0) * Math.PI / 180;
      const followRot = vals.followRotation ?? true;

      const listenerX = go.transform.position.x;
      const listenerY = go.transform.position.y;
      const worldRot = -go.transform.rotationRadians;

      this._engine.updateListenerFrom2D(
        listenerX,
        listenerY,
        worldRot,
        invertX,
        invertY,
        rotOffset,
        followRot,
      );

      this._activeListenerGuid = go.guid;
      return; // Only one listener
    }

    // No active listener — mute
    this._engine.setMasterVolume(0);
    this._activeListenerGuid = null;
  }

  private _updateSources(deltaTime: number): void {
    if (!this._level) return;

    for (const go of this._level.getAllObjects()) {
      const values = go.getComponentData('core.AudioSource');
      if (!values) continue;
      const mapping = this._guidToVoice.get(go.guid);
      if (!mapping) continue;

      const voices = this._engine.getActiveVoices();
      const voice = voices.find((v) => v.id === mapping.voiceId);
      if (!voice) continue;

      // Update volume / mute / pitch at runtime
      voice.volume = values.mute ? 0 : (values.volume ?? 1);
      voice.pitch = values.pitch ?? 1;

      // Update spatial position (with velocity tracking for doppler)
      if (values.spatialBlend) {
        voice.updatePositionTracked(
          go.transform.position.x,
          0,
          -go.transform.position.y,
          deltaTime,
        );
      }
    }
  }
}
